// Export a quest to PDF.
//
// We don't pull in a PDF library — instead we build a self-contained HTML
// document (text + images embedded as data URIs) and hand it to the browser's
// native print-to-PDF. That keeps Cyrillic typography and inline images exactly
// as the browser renders them, with zero new dependencies.
//
// The section layout mirrors scripts/convert.py. The extra requirement here is
// that field images are placed at the END of their own section (e.g. after the
// "Описание" text, or after a location's "После ответа" text).

import { renderRouteMaps } from './routeMap';

// Images are stored per (location, field_type). For quest-info the location key
// is the literal string 'quest_info'; for locations it's the location uuid.
function buildImageIndex(images) {
  // A field holds an ordered gallery, so each key maps to an ARRAY of images
  // (the /api/quest-images list already comes sorted by gallery order).
  const index = {};
  for (const img of images || []) {
    const locKey = img.location_id === 'quest_info' ? 'quest_info' : img.location_uuid;
    if (!locKey || !img.field_type) continue;
    const key = `${locKey}|${img.field_type}`;
    (index[key] = index[key] || []).push(img);
  }
  return index;
}

function imageRequestUrl(img) {
  return `/api/image-file/${encodeURIComponent(img.id)}`;
}

// Fetch one image and inline it as a data URI so the print document is fully
// self-contained (no async image loads racing the print dialog).
async function fetchImageAsDataUri(img) {
  try {
    const res = await fetch(imageRequestUrl(img), { credentials: 'include' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

function escapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Quest text is stored as HTML (the editors escape it only for display). For the
// PDF we render that HTML directly, but strip anything executable/structural we
// don't want leaking into the print document.
function sanitizeRichText(html) {
  if (!html) return '';
  return String(html)
    .replace(/<\s*(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .trim();
}

export async function exportQuestToPdf({ questInfo, locations, questId, table }) {
  if (!questInfo) throw new Error('No quest data to export');

  // Pull the image manifest, then inline every image as a data URI.
  let images = [];
  try {
    const res = await fetch(
      `/api/quest-images/${encodeURIComponent(questId)}?table=${encodeURIComponent(table)}`,
      { credentials: 'include' }
    );
    if (res.ok) images = await res.json();
  } catch (e) {
    images = [];
  }
  const imageIndex = buildImageIndex(images);
  const dataUriCache = {}; // image id -> data URI
  await Promise.all(
    Object.values(imageIndex).flat().map(async (img) => {
      dataUriCache[img.id] = await fetchImageAsDataUri(img);
    })
  );

  // <img> blocks for a field's gallery (in order), or '' if none loaded.
  const imageBlock = (locKey, fieldType) => {
    const key = `${locKey}|${fieldType}`;
    return (imageIndex[key] || [])
      .map((img) => dataUriCache[img.id])
      .filter(Boolean)
      .map((uri) => `<div class="field-image"><img src="${uri}" alt="" /></div>`)
      .join('');
  };

  // A labelled field followed by its image (the image lands at the end of the
  // section, per the export requirement).
  const field = (locKey, fieldType, value, { label, heading } = {}) => {
    const text = sanitizeRichText(value);
    const img = imageBlock(locKey, fieldType);
    if (!text && !img) return '';
    let body = '';
    if (heading) body += `<h3>${escapeHtml(heading)}</h3>`;
    if (text) {
      body += label
        ? `<p class="labelled"><span class="label">${escapeHtml(label)}</span> ${text}</p>`
        : `<div class="rich">${text}</div>`;
    }
    body += img;
    return `<section class="field">${body}</section>`;
  };

  // Build the route maps (overview + detail) from location coordinates.
  let routeMaps = null;
  try {
    routeMaps = await renderRouteMaps(locations);
  } catch (e) {
    routeMaps = null;
  }

  const parts = [];
  parts.push(`<h1>${escapeHtml(questInfo.quest_name || 'Quest')}</h1>`);

  if (routeMaps && (routeMaps.overview || routeMaps.detailed)) {
    const maps = [];
    if (routeMaps.overview) {
      maps.push(`<figure class="route-map"><img src="${routeMaps.overview}" alt="Карта маршрута — обзор" /></figure>`);
    }
    if (routeMaps.detailed) {
      maps.push(`<figure class="route-map"><img src="${routeMaps.detailed}" alt="Карта маршрута — детально" /></figure>`);
    }
    parts.push(`<div class="route-maps">${maps.join('')}</div>`);
  }

  // Quest-level intro.
  parts.push(field('quest_info', 'quest_description', questInfo.quest_description));

  if (questInfo.promo_long_quest_description) {
    parts.push(
      field('quest_info', 'promo_long_quest_description', questInfo.promo_long_quest_description, {
        heading: 'Описание',
      })
    );
  }

  // Locations.
  parts.push('<h2>Локации</h2>');
  (locations || []).forEach((loc, i) => {
    const locKey = loc.uuid;
    const inner = [];
    inner.push(`<h3 class="location-title">${i + 1}. ${escapeHtml(loc.name || '')}</h3>`);
    inner.push(imageBlock(locKey, 'name'));
    inner.push(field(locKey, 'description', loc.description));
    inner.push(field(locKey, 'task', loc.task, { label: 'Задание:' }));
    if (loc.hint && loc.hint !== 'Для данного задания подсказка не предусмотрена') {
      inner.push(field(locKey, 'hint', loc.hint, { label: 'Подсказка:' }));
    }
    if (loc.answer_variations && loc.answer_variations.length) {
      inner.push(field(locKey, 'answer_variations', loc.answer_variations[0], { label: 'Ответ:' }));
    }
    inner.push(field(locKey, 'after_message', loc.after_message, { label: 'После ответа:' }));
    parts.push(`<section class="location">${inner.filter(Boolean).join('\n')}</section>`);
  });

  // Final message.
  parts.push(field('quest_info', 'final_message', questInfo.final_message, { heading: 'Финал' }));

  const docHtml = `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(questInfo.quest_name || 'Quest')}</title>
<style>
  @page { margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #1a1a1a; line-height: 1.5; font-size: 12pt; max-width: 760px; margin: 0 auto; padding: 24px;
  }
  h1 { font-size: 22pt; margin: 0 0 12px; }
  h2 { font-size: 17pt; margin: 28px 0 10px; border-bottom: 2px solid #ddd; padding-bottom: 4px; }
  h3 { font-size: 14pt; margin: 14px 0 6px; }
  .location { margin: 0 0 18px; padding-bottom: 12px; border-bottom: 1px solid #eee; page-break-inside: avoid; }
  .location-title { margin-top: 0; }
  .field { margin: 0 0 8px; }
  .rich { margin: 4px 0; }
  .labelled { margin: 4px 0; }
  .label { font-weight: 700; }
  .field-image { margin: 10px 0 6px; page-break-inside: avoid; text-align: center; }
  /* Cap field images at roughly a third of an A4 page and centre them. */
  .field-image img { max-width: 100%; max-height: 90mm; width: auto; height: auto; border-radius: 6px; display: inline-block; }
  .route-maps { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin: 10px 0 20px; page-break-inside: avoid; }
  .route-map { flex: 1 1 320px; margin: 0; text-align: center; }
  .route-map img { max-width: 100%; max-height: 110mm; width: auto; height: auto; border: 1px solid #ddd; border-radius: 6px; display: inline-block; }
  p { margin: 4px 0; }
  @media print { body { padding: 0; } a { color: inherit; text-decoration: none; } }
</style>
</head>
<body>
${parts.filter(Boolean).join('\n')}
</body>
</html>`;

  // Hand the document to a hidden iframe and trigger the native print dialog
  // (where the user picks "Save as PDF"). An iframe avoids popup blockers that
  // would kill window.open().
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  await new Promise((resolve) => {
    iframe.onload = () => resolve();
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(docHtml);
    doc.close();
    // Fallback in case onload doesn't fire for a written document.
    setTimeout(resolve, 300);
  });

  const win = iframe.contentWindow;
  win.focus();
  win.print();

  // Clean up once the print dialog is dismissed.
  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };
  if (win.matchMedia) {
    const mql = win.matchMedia('print');
    mql.addEventListener('change', (e) => {
      if (!e.matches) setTimeout(cleanup, 500);
    });
  }
  setTimeout(cleanup, 60000);
}
